<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuth } from "@/features/auth/composables/useAuth";
import { useTypewriter } from "@/features/auth/composables/useTypewriter";
import { isMobilePlatform } from "@/shared/services/platform";
import { flushRoaming } from "@/shared/services/storage";
import { setupState } from "@/shared/services/setup-state";
import { useToast } from "@/shared/composables/useToast";
import BaseButton from "@/shared/components/BaseButton.vue";
import MicrosoftLogo from "@/shared/components/MicrosoftLogo.vue";

const router = useRouter();
const route = useRoute();
const toast = useToast();
const { init, loginWithSSO, loginRedirect, isLoggedIn, loginPopup } = useAuth();

const isLoading = ref(true);
const isLoggingIn = ref(false);
const reasonExpired = ref(false);

const featureMessages = [
  "Informações centralizadas em um lugar",
  "Flexivel e simples de utilizar",
  "Assinatura sempre atualizada",
];
const { displayText, isComplete, start } = useTypewriter(featureMessages);

onMounted(async () => {
  start();
  reasonExpired.value = route.query.reason === "expired";

  try {
    await Office.onReady();
    await init();

    if (isLoggedIn.value) {
      await navigateToCorrectPage();
    }
  } catch (err: any) {
    console.error("[Login] Falha na inicialização:", err?.message || err);
  } finally {
    isLoading.value = false;
  }
});

async function doLogin(): Promise<void> {
  isLoggingIn.value = true;

  if (isMobilePlatform()) {
    toast.info("Conectando via SSO...");
    try {
      await loginWithSSO();
      toast.info("Verificando dados...");
      await navigateToCorrectPage();
    } catch (err: any) {
      console.warn("[Login] SSO falhou, tentando redirect:", err?.message || err);
      loginRedirect();
    }
    return;
  }

  try {
    await loginPopup();
    await navigateToCorrectPage();
    return;  // ← SUCESSO: encerra aqui
  } catch (err: any) {
    const msg = err?.errorCode || err?.message || "";  // ← corrigido typo
    const isBlocked = msg.includes("popup") || msg.includes("Popup");
    console.warn("[Login] Popup %s, fallback para redirect.", isBlocked ? "bloqueado" : "falhou");
    toast.warning(isBlocked
      ? "Popup bloqueado. Redirecionando..."
      : "Falha no popup. Redirecionando...");
    loginRedirect();
  }
}

/**
 * Reespelha o snapshot local no roaming
 *
 * @param userDataLocal - Snapshot serializado vindo do `localStorage`.
 */
function syncLocalToRoaming(userDataLocal: string): void {
  if (!Office.context?.roamingSettings) return;

  Office.context.roamingSettings.set("helpitech_user_info", userDataLocal);
  void flushRoaming();
}

async function navigateToCorrectPage(): Promise<void> {
  const userDataLocal = localStorage.getItem("user_info");
  let userDataRoaming: string | null = null;
  if (Office.context?.roamingSettings) {
    userDataRoaming = Office.context.roamingSettings.get("helpitech_user_info");
  }

  if (userDataLocal && (!userDataRoaming || userDataRoaming === "")) {
    syncLocalToRoaming(userDataLocal);
  }

  router.push(setupState.isComplete() ? "/signatures" : "/profile");
}
</script>

<template>
  <div id="login-content" class="login-container" :class="{ hidden: isLoading || isLoggingIn }">
    <div class="login-bg" aria-hidden="true">
      <div class="login-bg-orb login-bg-orb--1"></div>
      <div class="login-bg-orb login-bg-orb--2"></div>
      <div class="login-bg-orb login-bg-orb--3"></div>
    </div>

    <div class="login-card">
      <div class="login-brand">
        <div class="login-logo-wrap">
          <img src="/assets/logo-filled.png" alt="Helpitech" class="login-logo" />
        </div>
        <h1 class="login-title">Helpitech Signature</h1>
        <p class="login-subtitle">
          Use sua conta Microsoft para sincronizar suas informações.
        </p>
      </div>

      <div v-if="reasonExpired" class="login-expired-warning">
        ⚠ Sua sessão expirou. Faça login novamente para atualizar seus dados.
      </div>

      <div class="login-features-typewriter" aria-live="polite">
        <v-icon
          v-show="isComplete"
          name="md-checkcircle-outlined"
          class="login-features-icon"
        />
        <span v-show="!isComplete" class="login-features-icon-placeholder" aria-hidden="true"></span>
        <span class="login-features-text">{{ displayText }}</span>
        <span class="login-features-caret" aria-hidden="true">|</span>
      </div>

      <BaseButton
        class="login-microsoft-btn"
        size="lg"
        block
        :disabled="isLoggingIn"
        @click="doLogin"
      >
        <MicrosoftLogo class="login-microsoft-logo" />
        <span class="login-microsoft-label">{{ isLoggingIn ? "Entrando..." : "Entrar com Microsoft" }}</span>
      </BaseButton>

      <p class="login-footer">
        Ao continuar, você concorda com os termos de uso da aplicação.
      </p>
    </div>
  </div>
</template>

<style scoped>
.login-container {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  text-align: center;
  padding: var(--spacing-lg);
  overflow: hidden;
}

.login-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.login-bg-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.35;
  will-change: transform;
}

.login-bg-orb--1 {
  width: 320px;
  height: 320px;
  top: -80px;
  left: -80px;
  background: #6E4BFA;
  animation: orb-drift-1 22s ease-in-out infinite alternate;
}

.login-bg-orb--2 {
  width: 260px;
  height: 260px;
  bottom: -60px;
  right: -60px;
  background: var(--colorBrandBackground);
  animation: orb-drift-2 28s ease-in-out infinite alternate;
}

.login-bg-orb--3 {
  width: 180px;
  height: 180px;
  top: 40%;
  left: 60%;
  background: var(--colorInfo);
  opacity: 0.22;
  animation: orb-drift-3 34s ease-in-out infinite alternate;
}

@keyframes orb-drift-1 {
  0%   { transform: translate(0, 0)        scale(1); }
  100% { transform: translate(80px, 60px)  scale(1.25); }
}

@keyframes orb-drift-2 {
  0%   { transform: translate(0, 0)         scale(1); }
  100% { transform: translate(-60px, -40px) scale(1.2); }
}

@keyframes orb-drift-3 {
  0%   { transform: translate(0, 0)         scale(1); }
  100% { transform: translate(-50px, 40px)  scale(1.4); }
}

.login-card {
  position: relative;
  z-index: 1;
  background: color-mix(in srgb, var(--colorBackground) 88%, transparent);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--colorBorderSubtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow8);
  padding: var(--spacing-xl) var(--spacing-lg);
  max-width: 360px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.login-brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: var(--spacing-md);
}

.login-logo-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  margin-bottom: var(--spacing-md);
  background: color-mix(in srgb, var(--colorBackgroundSubtle) 70%, transparent);
  box-shadow: var(--shadow4);
}

.login-logo {
  width: 40px;
  height: 40px;
}

.login-title {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--colorText);
  margin: 0 0 var(--spacing-xs) 0;
  letter-spacing: -0.01em;
}

.login-subtitle {
  color: var(--colorTextSecondary);
  margin: 0 0 var(--spacing-lg) 0;
  font-size: var(--font-size-sm);
  line-height: 1.5;
  max-width: 280px;
}

.login-features-typewriter {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  min-height: 24px;
  margin: 0 0 var(--spacing-lg) 0;
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--font-size-sm);
  color: var(--colorTextSecondary);
  text-align: center;
  width: 100%;
  flex-wrap: nowrap;
}

.login-features-icon {
  color: var(--colorSuccess);
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.login-features-icon-placeholder {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

.login-features-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.login-features-caret {
  color: var(--colorPrimary);
  animation: caret-blink 1s steps(2, start) infinite;
  margin-left: 1px;
}

@keyframes caret-blink {
  50% { opacity: 0; }
}

.login-expired-warning {
  color: var(--colorWarning);
  background: var(--colorWarningBackground);
  border: 1px solid var(--colorStatusWarningStroke1);
  border-radius: var(--radius-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  font-size: var(--font-size-sm);
  width: 100%;
  text-align: left;
}

.login-footer {
  margin-top: var(--spacing-lg);
  font-size: 11px;
  color: var(--colorTextTertiary);
  line-height: 1.4;
}

.login-microsoft-btn {
  position: relative;
  overflow: hidden;
  background-color: var(--colorBackground) !important;
  color: var(--colorText) !important;
  border: 1px solid var(--colorBorder) !important;
  transition: box-shadow var(--transition), transform var(--transition), border-color var(--transition);
}

.login-microsoft-btn:hover:not(:disabled) {
  border-color: var(--colorPrimary) !important;
  box-shadow: 0 0 0 2px var(--colorBorderFocus), var(--shadow4) !important;
}

.login-microsoft-btn:active:not(:disabled) {
  transform: scale(0.98);
}

.login-microsoft-btn::before {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    transparent 30%,
    color-mix(in srgb, var(--colorPrimary) 12%, transparent) 50%,
    transparent 70%
  );
  transform: translateX(-100%);
  transition: transform 700ms ease;
  pointer-events: none;
}

.login-microsoft-btn:hover:not(:disabled)::before {
  transform: translateX(100%);
}

.login-microsoft-btn .login-microsoft-logo {
  transition: transform var(--transition);
}

.login-microsoft-btn:hover:not(:disabled) .login-microsoft-logo {
  transform: rotate(-6deg) scale(1.08);
}

.login-microsoft-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 400px) {
  .login-card {
    box-shadow: var(--shadow4);
    padding: var(--spacing-lg) var(--spacing-md);
  }

  .login-bg-orb--3 { display: none; }
}
</style>
